import { useEffect, useRef, useState } from "react";

import { Grid } from "@chakra-ui/react";

import { RadioGroup } from "../../radio/radio";
import { Box, Stack, Text, Tooltip } from "../../internal/exports";

import { Radio } from "../../radio/radio";

import { QuestionWithHeaderAndFooter } from "../question";
import { QuestionContent } from "../question-content";
import { LikertQuestionProps } from "../types";

const TruncatedTooltipWrapper = ({
  text,
  children
}: {
  text: string;
  children: (ref: React.RefObject<React.ElementRef<"p">>) => React.ReactElement;
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<React.ElementRef<"p">>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollHeight > textRef.current.clientHeight + 1 ||
            textRef.current.scrollWidth > textRef.current.clientWidth + 1
        );
      }
    };

    checkTruncation();

    if (
      textRef.current &&
      typeof window !== "undefined" &&
      window.ResizeObserver
    ) {
      const resizeObserver = new window.ResizeObserver(() => {
        checkTruncation();
      });
      resizeObserver.observe(textRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [text]);

  const content = children(textRef);

  return (
    <Tooltip
      label={isTruncated ? text : ""}
      hasArrow
      placement="top"
      disabled={!isTruncated}
    >
      {content}
    </Tooltip>
  );
};

export const LikertQuestion = (props: LikertQuestionProps) => {
  const {
    gap,
    required,
    invalid,
    labelClassName,
    disabled,
    questionId,
    questionLabel,
    questionLabelType,
    accessibilityTextOfRequired,
    questionLabelProps,
    layoutProps,
    contentLayoutProps,
    answerLabelProps,
    answer,
    size,
    handleChange,
    statements,
    answerStackSpacing,
    answerGroupSpacing,
    answerBlockSpacing,
    options,
    answerProps,
    footerStackSpacing,
    note,
    maxNote,
    maxLength,
    errorMessage,
    errorMessageProps,
    noteProps,
    getLikertOptionDataTestId
  } = props;

  const headerFooterProps = {
    gap,
    required,
    invalid,
    layoutProps,
    labelClassName,
    questionId,
    disabled,
    questionLabel,
    questionLabelType,
    accessibilityTextOfRequired,
    questionLabelProps,
    footerStackSpacing,
    note,
    maxLength,
    maxNote,
    errorMessage,
    errorMessageProps,
    noteProps
  };

  return (
    <QuestionWithHeaderAndFooter {...headerFooterProps}>
      <QuestionContent gap={answerStackSpacing} {...contentLayoutProps}>
        {statements?.map((statement) => (
          <Box as="fieldset" key={statement.id}>
            <Stack gap={answerGroupSpacing} pl={{ lg: 6 }}>
              <Box
                as="legend"
                className="ehq-likertStatement"
                id={`ehq-${questionId}-${statement.id}-group`}
                {...answerLabelProps}
              >
                {statement.question}
              </Box>
              <RadioGroup
                value={answer[statement.id]}
                size={size}
                onValueChange={(details) =>
                  handleChange(details.value ?? "", statement.id)
                }
              >
                <Grid
                  templateColumns={{
                    base: "1fr",
                    lg: "repeat(auto-fill, minmax(135px, 1fr))"
                  }}
                  gap={answerBlockSpacing}
                  {...contentLayoutProps}
                  pl={{ lg: 4 }}
                >
                  {options &&
                    options.map((option) => (
                      <TruncatedTooltipWrapper
                        key={option.id}
                        text={option.name}
                      >
                        {(textRef) => (
                          <Box display="flex" alignItems="center">
                            <Radio
                              value={option.id}
                              size="xlIconWithLabelStyle"
                              {...answerProps}
                              data-testid={getLikertOptionDataTestId?.(
                                statement.id,
                                option.id
                              )}
                            >
                              <Text
                                ref={textRef}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                lineClamp={3}
                              >
                                {option.name}
                              </Text>
                            </Radio>
                          </Box>
                        )}
                      </TruncatedTooltipWrapper>
                    ))}
                </Grid>
              </RadioGroup>
            </Stack>
          </Box>
        ))}
      </QuestionContent>
    </QuestionWithHeaderAndFooter>
  );
};
